import { getElementTag } from '@nrk/dh-server-libs/get-element-tag'
import LevendeStillbilde from '@nrk/dh-basis-levende-stillbilde'
import Bildesveip from './components/bildesveip.js'
import Wrapper from './components/wrapper.js'
import { createTracker } from '@nrk/dh-snowplow'
import { EventSchema, ViewportEventKind } from '@nrk/snowplow-web'
import './client.css'
import './components/bildesveip.module.css'

const tag = getElementTag(__APP_NAME__, __APP_VERSION__)

function safeDefine(elementName, constructor) {
  if (typeof customElements.get(elementName) === 'undefined') {
    customElements.define(elementName, constructor)
  }
}

export class AppElement extends HTMLElement {
  name = tag
  #entryObserver
  #exitObserver
  #tracker

  connectedCallback() {
    this.#defineCustomElements()
    this.#createTracker()

    this.#startViewportLogger()
    this.addEventListener('dh-basis-sveip-active-changed', this.#handleActiveChanged)
  }

  disconnectedCallback() {
    this.#stopViewportLogger()
    this.removeEventListener('dh-basis-sveip-active-changed', this.#handleActiveChanged)
  }

  async #defineCustomElements() {
    safeDefine('dh-bildesveip', Bildesveip)
    safeDefine('dh-bildesveip-wrapper', Wrapper)
    safeDefine('dh-bs-levende-stillbilde', LevendeStillbilde)

    if (this.features.kart && typeof customElements.get('dhbs-basis-kart') === 'undefined') {
      const BasisKart = (await import('./kart.js' /* webpackChunkName: "dh-basis-kart" */)).default
      safeDefine('dh-bs-kart', BasisKart)
    }
  }

  #createTracker() {
    if (process.env.DEV) {
      import('@nrk/dh-snowplow/mock').then(({ mock }) => mock()).catch(console.error)
    }

    this.#tracker = createTracker('dh-bildesveip', {
      environment: process.env.ENVIRONMENT,
      contentContext: {
        source: 'pp',
        id: this.dataset.selfId,
      },
    })
  }

  #handleActiveChanged = (e) => {
    this.#tracker?.trackSelfDescribingEvent({
      schema: EventSchema.StepEvent,
      data: {
        current: e.detail.nextIndex + 1,
        total: this.#scrollContainerChildren.length,
      },
    })
  }

  #startViewportLogger() {
    this.#entryObserver = new IntersectionObserver((entries) => {
      if (!entries[0]?.isIntersecting) {
        return
      }
      this.#entryObserver?.disconnect()
      this.#exitObserver?.observe(this)
      this.#tracker?.trackSelfDescribingEvent({
        schema: EventSchema.ViewportEvent,
        data: {
          kind: ViewportEventKind.Entry,
        },
      })
    }, {})

    this.#exitObserver = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) {
          return
        }
        this.#exitObserver?.disconnect()
        this.#tracker?.trackSelfDescribingEvent({
          schema: EventSchema.ViewportEvent,
          data: {
            kind: ViewportEventKind.Exit,
          },
        })
      },
      {
        threshold: [1],
        // `margin-block: 3000px -100%` setter opp et rektangel over skjermen
        // `margin-inline: 10%` sikrer at komponenter som stikker litt ut på høyre og venstre side trigger threshold på 1
        rootMargin: '3000px 10% -100% 10%',
      },
    )

    this.#entryObserver.observe(this)
  }

  #stopViewportLogger() {
    this.#entryObserver?.disconnect()
    this.#exitObserver?.disconnect()
  }

  get features() {
    return JSON.parse(this.getAttribute('features'))
  }

  get #scrollContainerChildren() {
    return this.querySelectorAll(`[data-scroll-container] > *`)
  }
}

safeDefine(tag, AppElement)
